import { useRef, useState } from "react";
import { type AffiliateRecord, uploadPhoto, resolvePhotoUrl } from "@/lib/affiliate";
import { SOCIAL_PLATFORMS } from "@/lib/social-utils";

type SocialState = Record<string, { value: string; set: (v: string) => void }>;

interface PhotoState {
  fileRef: React.RefObject<HTMLInputElement | null>;
  photoUrl: string;
  photoPreview: string;
  uploading: boolean;
  handlePhotoSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface AffiliateFormState extends PhotoState {
  name: string;
  setName: (v: string) => void;
  bio: string;
  setBio: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  secondaryEmail: string;
  setSecondaryEmail: (v: string) => void;
  socialState: SocialState;
  error: string;
  setError: (v: string) => void;
  submitting: boolean;
  setSubmitting: (v: boolean) => void;
}

export function useAffiliateForm(affiliate?: AffiliateRecord): AffiliateFormState {
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(affiliate?.name ?? "");
  const [bio, setBio] = useState(affiliate?.bio ?? "");
  const [phone, setPhone] = useState(affiliate?.phone ?? "");
  const [secondaryEmail, setSecondaryEmail] = useState(affiliate?.secondaryEmail ?? "");
  const [photoUrl, setPhotoUrl] = useState(affiliate?.photoUrl ?? "");
  const [photoPreview, setPhotoPreview] = useState(resolvePhotoUrl(affiliate?.photoUrl ?? null));
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [instagram, setInstagram] = useState(affiliate?.socialInstagram ?? "");
  const [youtube, setYoutube] = useState(affiliate?.socialYoutube ?? "");
  const [tiktok, setTiktok] = useState(affiliate?.socialTiktok ?? "");
  const [linkedin, setLinkedin] = useState(affiliate?.socialLinkedin ?? "");
  const [twitter, setTwitter] = useState(affiliate?.socialTwitter ?? "");
  const [website, setWebsite] = useState(affiliate?.socialWebsite ?? "");

  const socialState: SocialState = {
    instagram: { value: instagram, set: setInstagram },
    youtube: { value: youtube, set: setYoutube },
    tiktok: { value: tiktok, set: setTiktok },
    linkedin: { value: linkedin, set: setLinkedin },
    twitter: { value: twitter, set: setTwitter },
    website: { value: website, set: setWebsite },
  };

  async function handlePhotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    setUploading(true);
    setError("");
    try {
      const result = await uploadPhoto(file);
      setPhotoUrl(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload photo");
      setPhotoPreview(resolvePhotoUrl(affiliate?.photoUrl ?? null));
      setPhotoUrl(affiliate?.photoUrl ?? "");
    } finally {
      setUploading(false);
    }
  }

  return {
    fileRef,
    name,
    setName,
    bio,
    setBio,
    phone,
    setPhone,
    secondaryEmail,
    setSecondaryEmail,
    photoUrl,
    photoPreview,
    uploading,
    handlePhotoSelect,
    socialState,
    error,
    setError,
    submitting,
    setSubmitting,
  };
}

/**
 * Build the socials payload from social state, filtering out empty values.
 */
export function buildSocialsPayload(socialState: SocialState): Record<string, string> | undefined {
  const socials: Record<string, string> = {};
  for (const platform of SOCIAL_PLATFORMS) {
    const value = socialState[platform.key]?.value;
    if (value) socials[platform.key] = value;
  }
  return Object.keys(socials).length > 0 ? socials : undefined;
}
